import { useCallback } from 'react';

import { billingApi, billingEncountersApi } from '../store/api';
import { billingV2Api } from '../store/v2-api';

/**
 * Warming the workbench's first fetch wave, for whoever knows a claim is
 * about to be opened.
 *
 * Extracted from `useWorklistRowPrefetch` (GDN-3201) when the claim queue
 * (GDN-3322) needed the same three prefetches from a second place. The row
 * hook's own guards — hover-capable pointers only, once per row per mount —
 * stayed with it, because they describe HOVER; they are not properties of
 * warming a cache. The queue's arrival prefetch has its own reasons to fire
 * and its own reasons to skip.
 *
 * The one rule that belongs to the prefetch itself and travels with it: the
 * workspace arg carries BOTH ids, because the workbench subscribes with both
 * (`useClaimWorkspaceModel`) and RTK Query's cache key IS the serialized arg.
 * An encounterId-only prefetch warms an entry the workbench never reads — a
 * request that costs everything and saves nothing.
 */

/** Don't re-request data fresher than this. */
export const CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS = 30;

export interface ClaimWorkspacePrefetchTarget {
  claimId: string;
  /** Null for a row with no encounter (imported claims) — the claim and
   *  certification reads still warm; the workspace GET cannot. */
  encounterId: string | null;
}

export function useClaimWorkspacePrefetch(): (target: ClaimWorkspacePrefetchTarget) => void {
  const prefetchWorkspace = billingEncountersApi.usePrefetch(
    'getV2ProviderBillingEncountersByEncounterIdClaimWorkspace',
    { ifOlderThan: CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS },
  );
  const prefetchClaim = billingApi.usePrefetch('getV2ProviderClaimsByClaimId', {
    ifOlderThan: CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS,
  });
  const prefetchCertification = billingV2Api.usePrefetch(
    'getV2ProviderClaimsByClaimIdCertification',
    { ifOlderThan: CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS },
  );

  return useCallback(
    ({ claimId, encounterId }: ClaimWorkspacePrefetchTarget) => {
      /* Mirrors the workbench's own mount wave (GDN-3199): claim and
         certification key on claim_id; the workspace GET needs the direct
         route's encounter_id. */
      prefetchClaim({ claimId });
      prefetchCertification({ claimId });
      if (encounterId) prefetchWorkspace({ claimId, encounterId });
    },
    [prefetchCertification, prefetchClaim, prefetchWorkspace],
  );
}
